import { create } from "zustand";
import { logEvent } from "@um/ui";
import type { FeedbackContentContext } from "@um/contracts";
import { getRouteTitle } from "@/lib/routeTitles";

export interface CapturedScreenshot {
  dataUrl: string;
  width: number;
  height: number;
}

interface ReportPageState {
  isOpen: boolean;
  capturing: boolean;
  screenshot: CapturedScreenshot | null;
  /** Path of the page being reported, captured at open time. */
  pagePath: string;
  /** Title of the page being reported, when the route has a known title. */
  pageTitle?: string;
  /**
   * When set, the report targets a specific piece of AI-generated content
   * (e.g. a character) rather than the page in general. Google Play's
   * AI-Generated Content policy requires users to be able to flag offensive
   * generated content from inside the app.
   */
  content: FeedbackContentContext | null;
  /**
   * Removal handler supplied by the reporting surface (e.g. the character
   * page soft-deleting the reported version). When present, the drawer
   * offers a default-on "also remove this from my app" checkbox for safety
   * categories and invokes this after the report is submitted.
   */
  removeAction: (() => Promise<void>) | null;
  /**
   * Capture a screenshot of the current page (best effort) and open the
   * report drawer. Callers should first close any menu/sheet covering the
   * page so it isn't in the shot. Pass `content` to report a specific piece
   * of content instead of the page in general, and `removeAction` to let
   * the reporter also remove that content.
   */
  openReport: (options?: {
    content?: FeedbackContentContext;
    removeAction?: () => Promise<void>;
  }) => Promise<void>;
  close: () => void;
  removeScreenshot: () => void;
}

/** Cap screenshot size to keep uploads small; page height can be very tall. */
const MAX_SCREENSHOT_DIMENSION = 1600;

async function capturePageScreenshot(): Promise<CapturedScreenshot | null> {
  try {
    // html2canvas-pro (not classic html2canvas): the app theme uses oklch()
    // colors from Tailwind 4, which the original library cannot parse.
    const { default: html2canvas } = await import("html2canvas-pro");
    const width = window.innerWidth;
    const height = window.innerHeight;
    const scale = Math.min(
      1,
      MAX_SCREENSHOT_DIMENSION / Math.max(width, height)
    );
    const canvas = await html2canvas(document.body, {
      // Capture only the visible viewport, not the full scroll height.
      width,
      height,
      x: window.scrollX,
      y: window.scrollY,
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
    });
    return {
      dataUrl: canvas.toDataURL("image/jpeg", 0.8),
      width: canvas.width,
      height: canvas.height,
    };
  } catch (error) {
    console.warn("[reportPage] Screenshot capture failed", error);
    return null;
  }
}

export const useReportPageStore = create<ReportPageState>((set) => ({
  isOpen: false,
  capturing: false,
  screenshot: null,
  pagePath: "",
  pageTitle: undefined,
  content: null,
  removeAction: null,

  openReport: async (options) => {
    const pagePath = window.location.pathname;
    const content = options?.content ?? null;
    set({
      capturing: true,
      pagePath,
      pageTitle: getRouteTitle(pagePath),
      content,
      removeAction: options?.removeAction ?? null,
    });
    logEvent("um_report_page_opened", {
      path: pagePath,
      ...(content ? { contentType: content.type } : {}),
    });

    const screenshot = await capturePageScreenshot();
    set({ screenshot, capturing: false, isOpen: true });
  },

  close: () =>
    set({ isOpen: false, screenshot: null, content: null, removeAction: null }),

  removeScreenshot: () => set({ screenshot: null }),
}));
